import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Gavel, Sparkles, ListChecks, ChevronDown, BookmarkPlus, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { SiteHeader } from "@/components/Logo";
import ManageListsDialog from "@/components/ManageListsDialog";
import { createDraft } from "@/lib/api";
import { saveSession } from "@/lib/storage";
import { getListNames, getList, saveList } from "@/lib/lists";

const SAMPLE_GOLFERS = [
  "Scottie Scheffler",
  "Rory McIlroy",
  "Xander Schauffele",
  "Ludvig Åberg",
  "Collin Morikawa",
  "Wyndham Clark",
  "Viktor Hovland",
  "Patrick Cantlay",
  "Bryson DeChambeau",
  "Jon Rahm",
  "Justin Thomas",
  "Hideki Matsuyama",
  "Brooks Koepka",
  "Tony Finau",
  "Sungjae Im",
  "Tommy Fleetwood",
];

export default function CreateDraft() {
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "Palm Breezy Open Draft",
    host_username: "",
    budget: 200,
    roster_size: 6,
    nomination_seconds: 20,
    bid_extension_seconds: 8,
    nomination_window_seconds: 0,
  });
  const [playersText, setPlayersText] = useState("");
  const [scheduledLocal, setScheduledLocal] = useState("");
  const [savedNames, setSavedNames] = useState(() => getListNames());
  const [manageOpen, setManageOpen] = useState(false);

  const refreshSaved = () => setSavedNames(getListNames());
  useEffect(() => {
    refreshSaved();
  }, [manageOpen]);

  const currentGolfers = () =>
    playersText
      .split(/\r?\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);

  const loadList = (name) => {
    const arr = getList(name);
    if (!arr) return;
    setPlayersText(arr.join("\n"));
    toast.success(`Loaded "${name}" (${arr.length} golfer${arr.length === 1 ? "" : "s"})`);
  };

  const saveCurrentAsList = () => {
    const golfers = currentGolfers();
    if (golfers.length === 0) {
      toast.error("Add some golfers first");
      return;
    }
    const suggested = `My field ${new Date().toLocaleDateString()}`;
    const name = window.prompt("Name this list:", suggested);
    if (!name || !name.trim()) return;
    if (savedNames.includes(name.trim())) {
      if (!window.confirm(`"${name.trim()}" already exists. Overwrite?`)) return;
    }
    saveList(name.trim(), golfers);
    refreshSaved();
    toast.success(`Saved "${name.trim()}"`);
  }; // datetime-local string

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.host_username.trim()) {
      toast.error("Enter your name to host the draft");
      return;
    }
    const players = playersText
      .split(/\r?\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (players.length < 2) {
      toast.error("Add at least 2 golfers to the field");
      return;
    }
    setSubmitting(true);
    try {
      let scheduledIso = null;
      if (scheduledLocal) {
        const d = new Date(scheduledLocal);
        if (!isNaN(d.getTime())) {
          scheduledIso = d.toISOString();
        }
      }
      const res = await createDraft({
        ...form,
        budget: Number(form.budget),
        roster_size: Number(form.roster_size),
        nomination_seconds: Number(form.nomination_seconds),
        bid_extension_seconds: Number(form.bid_extension_seconds),
        nomination_window_seconds: Number(form.nomination_window_seconds) || 0,
        players,
        scheduled_start_at: scheduledIso,
      });
      saveSession(res.code, res.participant_id, form.host_username.trim());
      toast.success(`Draft room ${res.code} created!`);
      nav(`/draft/${res.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create draft");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-pbo-cream">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
            Host a draft
          </div>
          <h1 className="font-serif text-pbo-green text-4xl sm:text-5xl mt-2">
            Set the field, set the stakes.
          </h1>
        </div>

        <form onSubmit={onSubmit} className="grid lg:grid-cols-3 gap-6">
          {/* Left – settings */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="Draft details" subtitle="Name your draft and tell us who's hosting.">
              <Field label="Draft name" id="draft-name">
                <Input
                  id="draft-name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  className="bg-white"
                  data-testid="draft-name-input"
                />
              </Field>
              <Field label="Your name (host)" id="host-name">
                <Input
                  id="host-name"
                  value={form.host_username}
                  onChange={(e) => set("host_username", e.target.value)}
                  placeholder="e.g. Big Cat"
                  className="bg-white"
                  data-testid="host-name-input"
                />
              </Field>
            </Card>

            <Card title="Rules" subtitle="Pick the budget, roster size and timer behaviour.">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Budget per team ($)" id="budget">
                  <Input
                    id="budget"
                    type="number"
                    min={10}
                    value={form.budget}
                    onChange={(e) => set("budget", e.target.value)}
                    className="bg-white font-mono"
                    data-testid="budget-input"
                  />
                </Field>
                <Field label="Golfers per team" id="roster">
                  <Input
                    id="roster"
                    type="number"
                    min={1}
                    max={20}
                    value={form.roster_size}
                    onChange={(e) => set("roster_size", e.target.value)}
                    className="bg-white font-mono"
                    data-testid="roster-size-input"
                  />
                </Field>
                <Field label="Initial timer (seconds)" id="nom-seconds">
                  <Input
                    id="nom-seconds"
                    type="number"
                    min={5}
                    max={120}
                    value={form.nomination_seconds}
                    onChange={(e) => set("nomination_seconds", e.target.value)}
                    className="bg-white font-mono"
                    data-testid="nomination-seconds-input"
                  />
                </Field>
                <Field label="Anti-snipe extension (seconds)" id="ext-seconds">
                  <Input
                    id="ext-seconds"
                    type="number"
                    min={3}
                    max={30}
                    value={form.bid_extension_seconds}
                    onChange={(e) => set("bid_extension_seconds", e.target.value)}
                    className="bg-white font-mono"
                    data-testid="extension-seconds-input"
                  />
                </Field>
                <Field label="Nomination time limit (seconds, 0 = no limit)" id="nom-window">
                  <Input
                    id="nom-window"
                    type="number"
                    min={0}
                    max={600}
                    value={form.nomination_window_seconds}
                    onChange={(e) => set("nomination_window_seconds", e.target.value)}
                    className="bg-white font-mono"
                    data-testid="nomination-window-input"
                  />
                </Field>
              </div>
              <Field label="Scheduled start (optional — leave blank to start whenever)" id="sched-time">
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    id="sched-time"
                    type="datetime-local"
                    value={scheduledLocal}
                    onChange={(e) => setScheduledLocal(e.target.value)}
                    className="bg-white font-mono w-auto min-w-[230px]"
                    data-testid="scheduled-time-input"
                  />
                  {scheduledLocal && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setScheduledLocal("")}
                      className="h-9 text-xs text-pbo-muted hover:text-pbo-green"
                      data-testid="clear-scheduled-time-btn"
                    >
                      Clear
                    </Button>
                  )}
                  <span className="text-[11px] text-pbo-muted">
                    Shown as a countdown in the lobby. The host can still start anytime.
                  </span>
                </div>
              </Field>
            </Card>

            <Card
              title="The field"
              subtitle="One golfer per line, or comma-separated. Duplicates are removed."
            >
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPlayersText(SAMPLE_GOLFERS.join("\n"))}
                  className="border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream"
                  data-testid="use-sample-golfers-btn"
                >
                  <Sparkles className="w-4 h-4 mr-1.5" />
                  Use sample PGA Tour field
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-pbo-gold text-pbo-gold hover:bg-pbo-gold hover:text-pbo-ink"
                      data-testid="my-lists-btn"
                    >
                      <ListChecks className="w-4 h-4 mr-1.5" />
                      My lists
                      <ChevronDown className="w-3 h-3 ml-1.5 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="bg-white border-pbo-border w-64 max-h-80 overflow-y-auto"
                    data-testid="my-lists-menu"
                  >
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-pbo-muted">
                      Saved lists ({savedNames.length})
                    </DropdownMenuLabel>
                    {savedNames.length === 0 ? (
                      <div className="px-2 py-1.5 text-xs text-pbo-muted italic">
                        Nothing saved yet.
                      </div>
                    ) : (
                      savedNames.map((n) => (
                        <DropdownMenuItem
                          key={n}
                          onClick={() => loadList(n)}
                          className="cursor-pointer text-pbo-ink focus:bg-pbo-paper focus:text-pbo-green"
                          data-testid={`load-list-${n}`}
                        >
                          <ListChecks className="w-3.5 h-3.5 mr-2 text-pbo-green" />
                          <span className="truncate">{n}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={saveCurrentAsList}
                      className="cursor-pointer text-pbo-green focus:bg-pbo-paper"
                      data-testid="save-current-list-btn"
                    >
                      <BookmarkPlus className="w-3.5 h-3.5 mr-2" />
                      Save current as new list…
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setManageOpen(true)}
                      className="cursor-pointer text-pbo-muted focus:bg-pbo-paper focus:text-pbo-green"
                      data-testid="manage-lists-btn"
                    >
                      <Settings2 className="w-3.5 h-3.5 mr-2" />
                      Manage lists…
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <span className="text-xs text-pbo-muted">
                  {playersText.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean).length} golfers
                </span>
              </div>
              <Textarea
                rows={12}
                value={playersText}
                onChange={(e) => setPlayersText(e.target.value)}
                placeholder={"Scottie Scheffler\nRory McIlroy\nXander Schauffele\n..."}
                className="bg-white font-mono text-sm leading-relaxed"
                data-testid="golfers-textarea"
              />
            </Card>
          </div>

          {/* Right – summary */}
          <aside className="space-y-4">
            <div className="sticky top-24">
              <div className="bg-pbo-green text-pbo-cream rounded-2xl p-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-pbo-gold-soft font-semibold">
                  Summary
                </div>
                <h3 className="font-serif text-2xl mt-1">{form.name || "Untitled Draft"}</h3>
                <Separator className="my-4 bg-pbo-gold/30" />
                <SummaryRow label="Host" value={form.host_username || "—"} />
                <SummaryRow label="Budget" value={`$${form.budget}`} mono />
                <SummaryRow label="Roster size" value={form.roster_size} mono />
                <SummaryRow label="Initial timer" value={`${form.nomination_seconds}s`} mono />
                <SummaryRow label="Anti-snipe" value={`${form.bid_extension_seconds}s`} mono />
                {scheduledLocal && (
                  <SummaryRow
                    label="Scheduled"
                    value={new Date(scheduledLocal).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  />
                )}
                <Separator className="my-4 bg-pbo-gold/30" />
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold h-12 rounded-full"
                  data-testid="submit-create-draft-btn"
                >
                  <Gavel className="w-4 h-4 mr-2" />
                  {submitting ? "Creating..." : "Open the draft room"}
                </Button>
                <p className="text-[11px] text-pbo-gold-soft/80 mt-3 text-center">
                  You&apos;ll get a 4-letter room code to share with your group.
                </p>
              </div>
            </div>
          </aside>
        </form>
      </div>

      <ManageListsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        onChange={refreshSaved}
      />
    </div>
  );
}

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white border border-pbo-border rounded-2xl p-6">
    <div className="mb-5">
      <h2 className="font-serif text-xl text-pbo-green">{title}</h2>
      {subtitle && <p className="text-sm text-pbo-muted mt-1">{subtitle}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5">
    <Label
      htmlFor={id}
      className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted"
    >
      {label}
    </Label>
    {children}
  </div>
);

const SummaryRow = ({ label, value, mono }) => (
  <div className="flex justify-between items-baseline py-1.5 text-sm">
    <span className="text-pbo-gold-soft/90 uppercase tracking-wide text-[11px]">
      {label}
    </span>
    <span className={mono ? "font-mono font-semibold" : "font-semibold"}>{value}</span>
  </div>
);
